package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * 选项卡控件。
 *
 * 结构：顶部 header bar（高度 [HEADER_HEIGHT]）+ 下方 content（仅 active tab 可见）。Tab 子项通过
 * [child] 收集，但 [children] 仅返回当前 active 的那个，让布局引擎只为可见 tab 分配空间。
 *
 * header bar 由 TabViewRenderer 自绘；header 点击命中由 InputDispatcher 的 TabView 特化分支处理；
 * content 区域由 UIRenderer 的 TabView 特化分支为 active tab 绘制原点加 +HEADER_HEIGHT 的 Y 偏移。
 */
class TabView private constructor() : UIElement<TabView>() {

    private val tabList: MutableList<Tab> = ArrayList()
    private var activeIndexValue: Int = 0
    private var onChangeHandler: UiEventHandler<ChangeEvent<Int>>? = null
    private var boundActiveIndexSignal: Signal<in Double>? = null

    // ===== 逐实例视觉覆盖（header 级别）；null = 回退主题 token，保持零回归 =====
    private var tabActiveBackgroundColorValue: Int? = null
    private var tabInactiveBackgroundColorValue: Int? = null
    private var tabActiveTextColorValue: Int? = null
    private var tabInactiveTextColorValue: Int? = null
    private var tabIndicatorColorValue: Int? = null
    private var headerHeightValue: Float? = null
    private var tabPaddingValue: Float? = null

    init {
        focusableField = true
        // 默认 onClick 由 InputDispatcher 在 TabView 特化分支调用：命中 header 时 selectTab(index)，
        // 命中 content 时事件继续冒泡到 active tab 子树
    }

    override fun onClick(handler: UiEventHandler<MouseEvent>?): TabView = super.onClick(handler)

    protected override fun self(): TabView = this

    override fun child(child: UIElement<*>): TabView {
        if (child is Tab) {
            tabList.add(child)
        } else {
            super.child(child)
        }
        return this
    }

    /** 仅返回当前 active 的 Tab，让布局/默认渲染只处理它。 */
    override fun children(): List<UIElement<*>> {
        if (tabList.isEmpty()) {
            return emptyList()
        }
        val idx = maxOf(0, minOf(activeIndexValue, tabList.size - 1))
        return listOf(tabList[idx])
    }

    /** 全量 tab 列表（不可变视图），供 Renderer / 命中测试使用。 */
    // 只读拷贝（readOnlyCopy 契约）：与迁移前 Collections.unmodifiableList 行为等价
    fun tabs(): List<Tab> = readOnlyCopy(tabList)

    fun activeIndex(i: Int): TabView {
        val max = maxOf(0, tabList.size - 1)
        val clamped = if (i < 0) 0 else minOf(i, max)
        val old = activeIndexValue
        activeIndexValue = clamped
        if (old != clamped) {
            boundActiveIndexSignal?.set(clamped.toDouble())
            onChangeHandler?.accept(ChangeEvent(old, clamped))
            val scheduler = getAnimationScheduler()
            if (scheduler != null && tabList.isNotEmpty()) {
                scheduler.attachSubTree(tabList[clamped])
            }
            // FR-30 B1：children() 返回值取决于 activeIndexValue（切 tab 即换子树），但本方法绕过 child()/replaceChildren()，
            // 须显式标布局脏，否则整树布局 memo 命中返回旧 tab 布局、新 tab 内容不被定位（消失 / 错位）。
            markLayoutDirty()
        }
        return this
    }

    fun activeIndex(): Int = activeIndexValue

    fun onChange(handler: UiEventHandler<ChangeEvent<Int>>?): TabView {
        onChangeHandler = handler
        return this
    }

    /** 双向绑定到响应式 Signal。 */
    fun bindActiveIndex(signal: Signal<in Double>): TabView {
        // 逆变投影 Signal<in Double>：接收任意可写入 Double 的信号（activeIndex 以 Double 回写），
        // 写入位放开可直接 set(Double)，读取位投影为 Any? 交由 toIntValue 解码——无需强转。
        boundActiveIndexSignal = signal
        lifecycleScopeField.register(
            Effect {
                val max = maxOf(0, tabList.size - 1)
                val i = toIntValue(signal.get())
                activeIndexValue = if (i < 0) 0 else minOf(i, max)
            },
        )
        return this
    }

    /** 由 InputDispatcher 调用：根据 header 点击位置切换 active tab。 */
    fun selectTab(index: Int) {
        activeIndex(index)
    }

    // ===== 视觉覆盖 setter / getter（fluent setter 返回 this，null 清除覆盖回退主题） =====

    fun tabActiveBackgroundColor(v: Int?): TabView {
        tabActiveBackgroundColorValue = v
        return this
    }

    fun tabActiveBackgroundColor(): Int? = tabActiveBackgroundColorValue

    fun tabInactiveBackgroundColor(v: Int?): TabView {
        tabInactiveBackgroundColorValue = v
        return this
    }

    fun tabInactiveBackgroundColor(): Int? = tabInactiveBackgroundColorValue

    fun tabActiveTextColor(v: Int?): TabView {
        tabActiveTextColorValue = v
        return this
    }

    fun tabActiveTextColor(): Int? = tabActiveTextColorValue

    fun tabInactiveTextColor(v: Int?): TabView {
        tabInactiveTextColorValue = v
        return this
    }

    fun tabInactiveTextColor(): Int? = tabInactiveTextColorValue

    fun tabIndicatorColor(v: Int?): TabView {
        tabIndicatorColorValue = v
        return this
    }

    fun tabIndicatorColor(): Int? = tabIndicatorColorValue

    fun headerHeight(v: Float?): TabView {
        headerHeightValue = v
        return this
    }

    fun headerHeight(): Float? = headerHeightValue

    /** 生效 Header 高度：覆盖优先，null 回退 [HEADER_HEIGHT]。渲染 / 布局原点 / 命中带 / 裁剪统一取此值。 */
    fun effectiveHeaderHeight(): Float = headerHeightValue ?: HEADER_HEIGHT

    fun tabPadding(v: Float?): TabView {
        tabPaddingValue = v
        return this
    }

    fun tabPadding(): Float? = tabPaddingValue

    private fun toIntValue(v: Any?): Int = when (v) {
        is Number -> v.toInt()
        else -> throw IllegalStateException("TabView 绑定的 activeIndex state 必须是数字: $v")
    }

    companion object {
        /** Header bar 固定高度（px）。 */
        const val HEADER_HEIGHT = 16f

        /** 创建一个选项卡控件。 */
        @JvmStatic
        fun create(): TabView = TabView()
    }
}
